import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit,
  Play,
  Copy,
  Trash2,
  FileText,
  User,
  ClipboardList,
  Target,
  Tag,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  TestTube2,
  Upload,
  ExternalLink,
  Loader2,
  Eye,
  XCircle,
  Home,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { formatDistanceToNow, format } from 'date-fns';
import { Breadcrumb } from '../../components/ui';

interface TestCase {
  id: string;
  name: string;
  code: string;
  category: string;
  complexity: string;
  condition: string;
  description?: string;
  tags: string[];
  isDraft: boolean;
  isActive: boolean;
  validatedAt?: string;
  patientInfo: {
    age: number;
    gender: string;
    chiefComplaint: string;
    medicalHistory: string[];
    currentMedications: string[];
    allergies: string[];
  };
  questionnaire: Array<{ id: string; question: string; type: string }>;
  answers: Array<{ question: string; answer: string }>;
  expectedOutcomes?: {
    suitability?: string;
    confidenceRange?: { min: number; max: number };
    requiredIssues?: Array<{ severity: string; keywordMatch: string }>;
    requiredCriteria?: Array<{ field: string; valueMatch: string; priority: string }>;
  };
  documents: Array<{
    id: string;
    originalName: string;
    mimeType: string;
    url: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; name: string; email: string };
  _count: { testRuns: number; documents: number };
}

interface TestRun {
  id: string;
  name: string;
  status: string;
  runType: 'MODEL_COMPARISON' | 'MODEL_STABILITY';
  isRevealed: boolean;
  createdAt: string;
  completedAt?: string;
  _count: { executions: number };
  executions: Array<{ status: string }>;
}

function ComplexityBadge({ complexity }: { complexity: string }) {
  const config: Record<string, { color: string; label: string }> = {
    SIMPLE: { color: 'bg-green-100 text-green-700', label: 'Simple' },
    MODERATE: { color: 'bg-blue-100 text-blue-700', label: 'Moderate' },
    COMPLEX: { color: 'bg-amber-100 text-amber-700', label: 'Complex' },
    EDGE_CASE: { color: 'bg-red-100 text-red-700', label: 'Edge Case' },
  };
  const cfg = config[complexity] || { color: 'bg-gray-100 text-gray-700', label: complexity };
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', cfg.color)}>
      {cfg.label}
    </span>
  );
}

function TestRunStatusBadge({ status }: { status: string }) {
  const config: Record<string, { color: string; label: string; icon: typeof CheckCircle }> = {
    DRAFT: { color: 'bg-gray-100 text-gray-600', label: 'Draft', icon: FileText },
    RUNNING: { color: 'bg-blue-100 text-blue-700', label: 'Running', icon: Loader2 },
    COMPLETED: { color: 'bg-green-100 text-green-700', label: 'Completed', icon: CheckCircle },
    FAILED: { color: 'bg-red-100 text-red-700', label: 'Failed', icon: XCircle },
    CANCELLED: { color: 'bg-gray-100 text-gray-600', label: 'Cancelled', icon: XCircle },
  };
  const cfg = config[status] || { color: 'bg-gray-100 text-gray-600', label: status, icon: AlertCircle };
  const Icon = cfg.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', cfg.color)}>
      <Icon className={clsx('h-3 w-3', status === 'RUNNING' && 'animate-spin')} />
      {cfg.label}
    </span>
  );
}

function StatusBadge({ isDraft, isActive, validatedAt }: { isDraft: boolean; isActive: boolean; validatedAt?: string }) {
  if (!isActive) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <Trash2 className="h-3 w-3" />
        Archived
      </span>
    );
  }
  if (isDraft) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <FileText className="h-3 w-3" />
        Draft
      </span>
    );
  }
  if (validatedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="h-3 w-3" />
        Validated
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <TestTube2 className="h-3 w-3" />
      Ready
    </span>
  );
}

export function TestCaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['validation', 'test-case', id],
    queryFn: () => api.get<{ data: TestCase }>(`/api/validation/test-cases/${id}`),
    enabled: !!id,
  });

  const testCase = response?.data;

  // Fetch test runs for this test case
  const { data: testRunsResponse } = useQuery({
    queryKey: ['validation', 'test-runs', 'by-test-case', id],
    queryFn: () => api.get<{ data: TestRun[]; total: number }>(`/api/validation/test-runs?testCaseId=${id}&limit=10`),
    enabled: !!id,
  });

  const testRuns = testRunsResponse?.data || [];

  // Redirect to edit mode if test case is a draft
  useEffect(() => {
    if (testCase?.isDraft && id) {
      navigate(`/validation/test-cases/${id}/edit`, { replace: true });
    }
  }, [testCase?.isDraft, id, navigate]);

  const duplicateMutation = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/api/validation/test-cases/${id}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-cases'] });
      navigate(`/validation/test-cases/${response.id}`);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => api.delete(`/api/validation/test-cases/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-cases'] });
      navigate('/validation/test-cases');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !testCase) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Test Case Not Found</h2>
        <p className="mt-1 text-sm text-gray-500">
          The test case you're looking for doesn't exist or has been deleted.
        </p>
        <Link
          to="/validation/test-cases"
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Test Cases
        </Link>
      </div>
    );
  }

  // If draft, show loading while redirecting (handled by useEffect above)
  if (testCase.isDraft) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Check if test case is complete enough to run tests
  const isComplete = !!(
    testCase.name &&
    testCase.code &&
    testCase.condition &&
    testCase.patientInfo &&
    (testCase.answers?.length ?? 0) > 0
  );

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Model Validation', href: '/validation', icon: Home },
    { label: 'Test Cases', href: '/validation/test-cases', icon: TestTube2 },
    { label: testCase.code || testCase.name },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{testCase.name}</h1>
            <StatusBadge
              isDraft={testCase.isDraft}
              isActive={testCase.isActive}
              validatedAt={testCase.validatedAt}
            />
          </div>
          <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
            <span className="font-mono">{testCase.code || '-'}</span>
            <span>•</span>
            <span>{testCase.category?.replace('_', ' ') || 'Uncategorized'}</span>
            <span>•</span>
            <ComplexityBadge complexity={testCase.complexity || 'MODERATE'} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isComplete ? (
            <Link
              to={`/validation/test-runs/new?testCaseId=${id}`}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
            >
              <Play className="h-4 w-4" />
              Run Test
            </Link>
          ) : (
            <button
              disabled
              className="inline-flex items-center gap-2 rounded-lg bg-gray-300 px-4 py-2 text-sm font-semibold text-gray-500 cursor-not-allowed"
              title="Complete the patient questionnaire before running tests"
            >
              <Play className="h-4 w-4" />
              Run Test
            </button>
          )}
          <Link
            to={`/validation/test-cases/${id}/edit`}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
          <button
            onClick={() => duplicateMutation.mutate()}
            disabled={duplicateMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (confirm('Are you sure you want to archive this test case?')) {
                archiveMutation.mutate();
              }
            }}
            disabled={archiveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-amber-100 p-2">
              <BarChart3 className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{testCase._count.testRuns}</p>
              <p className="text-sm text-gray-500">Test Runs</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{testCase._count.documents}</p>
              <p className="text-sm text-gray-500">Documents</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-100 p-2">
              <Clock className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {formatDistanceToNow(new Date(testCase.updatedAt), { addSuffix: true })}
              </p>
              <p className="text-sm text-gray-500">Last Updated</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Description */}
          {testCase.description && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Description
              </h2>
              <p className="text-gray-700">{testCase.description}</p>
            </div>
          )}

          {/* Condition */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <Target className="h-4 w-4" />
              Medical Condition
            </h2>
            <p className="text-gray-900">{testCase.condition}</p>
          </div>

          {/* Patient Info */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <User className="h-4 w-4" />
              Patient Information
            </h2>
            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-gray-500">Age</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {testCase.patientInfo?.age ?? '-'} years
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Gender</dt>
                <dd className="text-sm font-medium text-gray-900">{testCase.patientInfo?.gender || '-'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs text-gray-500">Chief Complaint</dt>
                <dd className="text-sm text-gray-900">{testCase.patientInfo?.chiefComplaint || '-'}</dd>
              </div>
              {(testCase.patientInfo?.medicalHistory?.length ?? 0) > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-xs text-gray-500">Medical History</dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {testCase.patientInfo?.medicalHistory?.map((item) => (
                      <span
                        key={item}
                        className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                      >
                        {item}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              {(testCase.patientInfo?.currentMedications?.length ?? 0) > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-xs text-gray-500">Current Medications</dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {testCase.patientInfo?.currentMedications?.map((item) => (
                      <span
                        key={item}
                        className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700"
                      >
                        {item}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              {(testCase.patientInfo?.allergies?.length ?? 0) > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-xs text-gray-500">Allergies</dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {testCase.patientInfo?.allergies?.map((item) => (
                      <span
                        key={item}
                        className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700"
                      >
                        {item}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Questionnaire */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <ClipboardList className="h-4 w-4" />
              Patient Questionnaire ({testCase.answers?.length ?? 0} questions)
            </h2>
            <div className="space-y-4">
              {(testCase.answers || []).map((qa, index) => (
                <div key={index} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Q{index + 1}
                    </span>
                    <p className="text-sm font-medium text-gray-900">{qa.question}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-green-600">
                      Answer
                    </span>
                    <p className="text-sm text-gray-700">
                      {/* Format multiselect answers (stored with ||| delimiter) for display */}
                      {qa.answer?.includes('|||') ? qa.answer.split('|||').join(', ') : qa.answer}
                    </p>
                  </div>
                </div>
              ))}
              {(!testCase.answers || testCase.answers.length === 0) && (
                <p className="text-sm text-gray-500 italic">No patient questionnaire answers yet.</p>
              )}
            </div>
          </div>

          {/* Expected Outcomes */}
          {testCase.expectedOutcomes && Object.keys(testCase.expectedOutcomes).length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                <Target className="h-4 w-4" />
                Expected Outcomes (Auto-Scoring)
              </h2>
              <dl className="grid gap-4 sm:grid-cols-2">
                {testCase.expectedOutcomes.suitability && (
                  <div>
                    <dt className="text-xs text-gray-500">Expected Suitability</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {testCase.expectedOutcomes.suitability.replace('_', ' ')}
                    </dd>
                  </div>
                )}
                {testCase.expectedOutcomes.confidenceRange && (
                  <div>
                    <dt className="text-xs text-gray-500">Confidence Range</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {testCase.expectedOutcomes.confidenceRange.min}% -{' '}
                      {testCase.expectedOutcomes.confidenceRange.max}%
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Test Runs */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                <Play className="h-4 w-4" />
                Test Runs ({testCase._count.testRuns})
              </h2>
              {isComplete && (
                <Link
                  to={`/validation/test-runs/new?testCaseId=${id}`}
                  className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700 font-medium"
                >
                  <Play className="h-3.5 w-3.5" />
                  New Run
                </Link>
              )}
            </div>
            {testRuns.length > 0 ? (
              <div className="space-y-3">
                {testRuns.map((run) => {
                  const completedCount = run.executions?.filter((e) => e.status === 'COMPLETED').length ?? 0;
                  const totalCount = run._count?.executions ?? run.executions?.length ?? 0;
                  return (
                    <Link
                      key={run.id}
                      to={`/validation/test-runs/${run.id}`}
                      className="block rounded-lg border border-gray-100 p-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{run.name}</span>
                            <TestRunStatusBadge status={run.status} />
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                            <span>{completedCount}/{totalCount} executions</span>
                            <span>•</span>
                            <span>{formatDistanceToNow(new Date(run.createdAt), { addSuffix: true })}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {run.status === 'COMPLETED' && !run.isRevealed && (
                            <Link
                              to={`/validation/test-runs/${run.id}/evaluate`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              Evaluate
                            </Link>
                          )}
                          {run.isRevealed && (
                            <Link
                              to={run.runType === 'MODEL_STABILITY'
                                ? `/validation/test-runs/${run.id}/stability-results`
                                : `/validation/test-runs/${run.id}/results`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                            >
                              <BarChart3 className="h-3.5 w-3.5" />
                              Results
                            </Link>
                          )}
                          <ExternalLink className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
                {testCase._count.testRuns > testRuns.length && (
                  <Link
                    to={`/validation/test-runs?testCaseId=${id}`}
                    className="block text-center text-sm text-amber-600 hover:text-amber-700 font-medium py-2"
                  >
                    View all {testCase._count.testRuns} test runs →
                  </Link>
                )}
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
                <TestTube2 className="mx-auto h-8 w-8 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No test runs yet</p>
                {isComplete && (
                  <Link
                    to={`/validation/test-runs/new?testCaseId=${id}`}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
                  >
                    <Play className="h-4 w-4" />
                    Run First Test
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tags */}
          {(testCase.tags?.length ?? 0) > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                <Tag className="h-4 w-4" />
                Tags
              </h2>
              <div className="flex flex-wrap gap-2">
                {testCase.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <FileText className="h-4 w-4" />
              Documents
            </h2>
            {(testCase.documents?.length ?? 0) > 0 ? (
              <ul className="space-y-2">
                {testCase.documents?.map((doc) => (
                  <li key={doc.id}>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 rounded-lg border border-gray-100 p-2 text-sm hover:bg-gray-50"
                    >
                      <FileText className="h-4 w-4 text-gray-400" />
                      <span className="flex-1 truncate text-gray-700">{doc.originalName}</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-200 p-4 text-center">
                <Upload className="mx-auto h-6 w-6 text-gray-300" />
                <p className="mt-1 text-xs text-gray-500">No documents attached</p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Metadata
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Created by</dt>
                <dd className="font-medium text-gray-900">{testCase.createdBy?.name || 'Unknown'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Created</dt>
                <dd className="text-gray-700">
                  {testCase.createdAt ? format(new Date(testCase.createdAt), 'PPp') : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Last updated</dt>
                <dd className="text-gray-700">
                  {testCase.updatedAt ? format(new Date(testCase.updatedAt), 'PPp') : '-'}
                </dd>
              </div>
              {testCase.validatedAt && (
                <div>
                  <dt className="text-gray-500">Validated</dt>
                  <dd className="text-gray-700">
                    {format(new Date(testCase.validatedAt), 'PPp')}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
